import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './AddFamily.css';
import useInactivityTimer from '../../hooks/useInactivityTimer';
import PageHeader from './PageHeader';
import { AxiosGet, AxiosPost, AxiosDelete } from '../http/Axios';

const UserAdminPage = () => {
  const inactivityMinutes =
    parseInt(process.env.REACT_APP_INACTIVITY_MINUTES || "20", 10);
  useInactivityTimer(inactivityMinutes);

  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [username, setUsername] = useState('');
  const [errors, setErrors] = useState('');

  const loggedInAs = sessionStorage.getItem("loggedInAs") || null;

  const allowedUsers = (process.env.REACT_APP_ALLOWED_USERS || "")
    .split(",")
    .map(u => u.trim().toLowerCase());

  useEffect(() => {
    if (!loggedInAs) {
      navigate('/login');
    } else {
      const username = loggedInAs.toLowerCase();
      if (!allowedUsers.includes(username)) {
        navigate('/login');
      }
    }
  }, [loggedInAs, navigate]);

  useEffect(() => {
    document.title = 'LeapFrog Lab User Admin Portal';
    if (loggedInAs) {
      loadUsers();
    }
  }, [loggedInAs]);

  const loadUsers = async () => {
    try {
      const res = await AxiosGet(process.env.REACT_APP_ADMIN_SERVICE + '/users');
      setUsers(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error('Error loading users', err);
      setUsers([]);
    }
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const handleLogout = async (e) => {
    e?.preventDefault();
    try {
      await fetch('/kidlab-reg/admin/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      console.error('Logout API failed:', err);
    }
    sessionStorage.clear();
    localStorage.removeItem('authToken');
    navigate('/login');
  };

  const validate = () => {
    if (!validateUsername(username)) {
      setErrors('Please enter a valid email address (e.g. user@leapfrog.com)');
      return false;
    }
    setErrors('');
    return true;
  };

  const validateUsername = (value) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      await AxiosPost(process.env.REACT_APP_ADMIN_SERVICE + '/users', { email: username.toLowerCase().trim(), enabled: true });
      setUsername('');
      loadUsers();
    } catch (err) {
      console.error('Error adding user', err);
      setErrors('Failed to add user. Maybe duplicate?');
    }
  };

  const handleDelete = async (id) => {
    try {
      await AxiosDelete(process.env.REACT_APP_ADMIN_SERVICE + `/users/${id}`);
      loadUsers();
    } catch (err) {
      console.error('Error deleting user', err);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  return (
    <div className="dashboard add-family">
      <PageHeader title="User Administration" />

      <form onSubmit={handleAdd} className="family-form">
        <main className="page-content">
          <div className="section">
            <div className="input-grid">
              <div className="input-group">
                <label htmlFor="username">
                  User Name: <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  name="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  maxLength="99"
                  className="input-size"
                />
                {errors && <p className="error-message">{errors}</p>}
              </div>
            </div>
          </div>

          <div className="section">
            <div className="buttons-container">
              <button type="submit" className="submit-button">
                Add User
              </button>
            </div>
          </div>
        </main>
      </form>

      <div className="section">
        <h3>Existing Users</h3>
        {users.length === 0 ? (
          <p>No users found.</p>
        ) : (
          <ul>
            {users.map((user) => (
              <li key={user.id}>
                {user.email} (enabled: {user.enabled ? 'Yes' : 'No'})
                <button
                  onClick={() => handleDelete(user.id)}
                  style={{ marginLeft: '1rem' }}
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default UserAdminPage;
